import axios, { AxiosHeaders } from 'axios';
import sessionAPI from '@/features/session/api/sessionAPI';
import appLocalStorage from '@/features/session/store/appLocalStorage';
import type { UserValidation } from '@/features/session/types/session.types';
import { jwtDecode } from 'jwt-decode';
import getRole from '@/utils/getRoles';
import type { Dispatch } from '@reduxjs/toolkit';
import {
  setResponseErrorStatus,
  signOut,
} from '@/features/session/store/sessionSlice';
import { setGlobalError } from '@/app/coreSlice';
import { config } from '@/config';

// Axios instance for authorized API requests, with base URL and default headers
const authorizedAPIInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  responseType: 'json',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Returns a base64-encoded user context string for API requests
const getContext = (): string | null => {
  const userAccess = appLocalStorage.getUserAccessProfile() as UserValidation;
  const role = getRole(userAccess);
  const token = appLocalStorage.getTokenData();
  const encodedToken: any = token?.id_token ? jwtDecode(token?.id_token) : null;

  let userProfile: {
    given_name: string;
    family_name: string;
    email: string;
    profile: string;
  } = {
    given_name: '',
    family_name: '',
    email: '',
    profile: '',
  };

  if (encodedToken) {
    userProfile = {
      given_name: encodedToken.given_name,
      family_name: encodedToken.family_name,
      email: encodedToken.email,
      profile: role as string,
    };
  } else {
    return null;
  }
  return btoa(JSON.stringify(userProfile));
};

// Request interceptor: adds Authorization header if token is present
authorizedAPIInstance.interceptors.request.use(
  async requestConfig => {
    if (/^\/chat\/session(?:\/|$)/.test(requestConfig.url ?? '')) {
      requestConfig.baseURL = config.formulaMateApiBaseUrl;
    }
    if (!requestConfig.headers) {
      requestConfig.headers = new AxiosHeaders();
    }
    const authToken = appLocalStorage.getAuthToken();
    if (authToken) {
      requestConfig.headers['Authorization'] = authToken;
    }
    return requestConfig;
  },
  error => {
    return Promise.reject(error);
  }
);

// Request interceptor: adds userContext param to every request
authorizedAPIInstance.interceptors.request.use(
  async requestConfig => {
    requestConfig.params = {
      userContext: getContext(),
      ...requestConfig.params,
    };
    return requestConfig;
  },
  error => {
    return Promise.reject(error);
  }
);

// Sets up response interceptor for token refresh and error handling
export const setupAPIInstanceInterceptor = (dispatch: Dispatch) => {
  authorizedAPIInstance.interceptors.response.use(
    response => response,
    async error => {
      if (/^\/chat\/session(?:\/|$)/.test(error.config?.url ?? '')) {
        // Chat payloads can contain sensitive formulas; never log the config.
        console.warn('FormulaMate API request failed:', error.response?.status);
      } else {
        console.log('API response error:', error);
      }
      const originalRequest = error.config;

      //Handle 401 error
      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        const tokenData = appLocalStorage.getTokenData();
        if (!tokenData?.refresh_token) {
          dispatch(setResponseErrorStatus(401));
          dispatch(signOut());
          return Promise.reject(error);
        }
        try {
          const tokens = await sessionAPI.refreshAccessToken(
            tokenData.refresh_token
          );
          if (tokens.status !== 200) {
            dispatch(setResponseErrorStatus(401));
            dispatch(signOut());
            return Promise.reject(error);
          }
          appLocalStorage.setTokenData(tokens.data);
          axios.defaults.headers.common.Authorization =
            appLocalStorage.getAuthToken() || '';
          return await authorizedAPIInstance(originalRequest);
        } catch (err) {
          dispatch(signOut());
          return Promise.reject(err);
        }
      }

      // Handle other response errors
      if (error.response) {
        const { status } = error.response;
        if (status === 401 || status === 403 || status === 404) {
          dispatch(setResponseErrorStatus(status));
          dispatch(signOut());
        }
      }

      return Promise.reject(error);
    }
  );
};

// Export the configured axios instance
export default authorizedAPIInstance;
